import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Bar, Line} from "react-chartjs-2";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from "chart.js";
import {format, startOfMonth} from "date-fns";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
    useTheme,
} from "@material-ui/core";
import {Alert, Autocomplete} from "@material-ui/lab";
import {DatePicker} from "@material-ui/pickers";
import {Title} from "react-admin";

import {client} from "../../dataProvider";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

type FunctionTotal = {ai_function: string | null; label: string; price: number};
type UserTotal = {user_id: number | null; username: string; price: number};
type DayTotal = {date: string; price: number};
type FunctionOption = {value: string; label: string};
type UserOption = {user_id: number; user__username: string};

type AiChargeDashboardData = {
    total_price: number;
    price_by_function: FunctionTotal[];
    price_by_user: UserTotal[];
    price_by_day: DayTotal[];
    available_ai_functions: FunctionOption[];
    available_users: UserOption[];
};

const currency = (value: number) => new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
}).format(value || 0);

const chartCurrency = (value: number | string) => currency(Number(value));

export const AiChargeDashboard: React.FC = () => {
    const theme = useTheme();
    const [dateFrom, setDateFrom] = useState<Date | null>(startOfMonth(new Date()));
    const [dateTo, setDateTo] = useState<Date | null>(new Date());
    const [aiFunction, setAiFunction] = useState("");
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
    const [data, setData] = useState<AiChargeDashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadDashboard = useCallback(async () => {
        if (!dateFrom || !dateTo) {
            setError("Seleziona entrambe le date.");
            return;
        }
        if (dateFrom > dateTo) {
            setError("La data finale deve essere successiva alla data iniziale.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await client("ai_charges", {
                method: "GET",
                params: {
                    date_from: format(dateFrom, "yyyy-MM-dd"),
                    date_to: format(dateTo, "yyyy-MM-dd"),
                    ...(aiFunction ? {ai_function: aiFunction} : {}),
                    ...(selectedUser ? {user: selectedUser.user_id} : {}),
                },
            });
            setData(response);
        } catch (loadError) {
            console.error("Errore nel caricamento della dashboard AI", loadError);
            setError("Impossibile caricare i dati della dashboard AI.");
        } finally {
            setLoading(false);
        }
    }, [aiFunction, dateFrom, dateTo, selectedUser]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const userChart = useMemo(() => ({
        labels: data?.price_by_user.map((item) => item.username) || [],
        datasets: [{
            label: "Spesa",
            data: data?.price_by_user.map((item) => item.price) || [],
            backgroundColor: "rgba(244, 67, 54, 0.7)",
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
            borderRadius: 5,
        }],
    }), [data, theme.palette.primary.main]);

    const dayChart = useMemo(() => ({
        labels: data?.price_by_day.map((item) => format(new Date(`${item.date}T12:00:00`), "dd/MM")) || [],
        datasets: [{
            label: "Spesa giornaliera",
            data: data?.price_by_day.map((item) => item.price) || [],
            borderColor: theme.palette.primary.main,
            backgroundColor: "rgba(244, 67, 54, 0.16)",
            fill: true,
            tension: 0.3,
            pointRadius: 3,
        }],
    }), [data, theme.palette.primary.main]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            tooltip: {callbacks: {label: (context: any) => currency(context.parsed.y ?? context.parsed.x ?? 0)}},
        },
        scales: {
            x: {grid: {display: false}, ticks: {color: theme.palette.text.secondary}},
            y: {
                beginAtZero: true,
                grid: {color: theme.palette.divider},
                ticks: {color: theme.palette.text.secondary, callback: chartCurrency},
            },
        },
    }), [theme.palette.divider, theme.palette.text.secondary]);

    const resetFilters = () => {
        setDateFrom(startOfMonth(new Date()));
        setDateTo(new Date());
        setAiFunction("");
        setSelectedUser(null);
    };

    return (
        <Box p={2}>
            <Title title="Dashboard AI"/>
            <Paper variant="outlined" style={{padding: 20, marginBottom: 20}}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePicker label="Data da" value={dateFrom} onChange={setDateFrom} inputVariant="outlined" format="dd/MM/yyyy" fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePicker label="Data a" value={dateTo} onChange={setDateTo} inputVariant="outlined" format="dd/MM/yyyy" fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField select label="Funzione AI" value={aiFunction} onChange={(event) => setAiFunction(event.target.value)} variant="outlined" fullWidth>
                            <MenuItem value="">Tutte le funzioni</MenuItem>
                            {data?.available_ai_functions.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            options={data?.available_users || []}
                            value={selectedUser}
                            onChange={(_, value) => setSelectedUser(value)}
                            getOptionLabel={(option) => option.user__username}
                            getOptionSelected={(option, value) => option.user_id === value.user_id}
                            renderInput={(params) => <TextField {...params} label="Utente" variant="outlined"/>}
                        />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Typography color="primary" style={{cursor: "pointer", whiteSpace: "nowrap"}} onClick={resetFilters}>Azzera</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" style={{marginBottom: 20}}>{error}</Alert>}
            {loading && <Box display="flex" justifyContent="center" p={4}><CircularProgress/></Box>}

            {!loading && data && <>
                <Grid container spacing={2} style={{marginBottom: 20}}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Spesa totale</Typography><Typography variant="h5">{currency(data.total_price)}</Typography></CardContent></Card>
                    </Grid>
                    {data.price_by_function.map((item) => (
                        <Grid item xs={12} sm={6} md={3} key={item.ai_function || "not-specified"}>
                            <Card><CardContent><Typography color="textSecondary">{item.label}</Typography><Typography variant="h5">{currency(item.price)}</Typography></CardContent></Card>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={2}>
                    <Grid item md={12}>
                        <Paper variant="outlined" style={{padding: 20}}>
                            <Typography variant="h6" gutterBottom>Spesa per utente</Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>Utenti ordinati dal costo più alto al più basso.</Typography>
                            <Box height={340}>
                                {data.price_by_user.length ? <Bar data={userChart} options={chartOptions}/> : <Typography align="center" color="textSecondary">Nessun dato disponibile.</Typography>}
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item md={12}>
                        <Paper variant="outlined" style={{padding: 20}}>
                            <Typography variant="h6" gutterBottom>Andamento giornaliero</Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>Spesa AI aggregata per giorno.</Typography>
                            <Box height={340}>
                                {data.price_by_day.length ? <Line data={dayChart} options={chartOptions}/> : <Typography align="center" color="textSecondary">Nessun dato disponibile.</Typography>}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </>}
        </Box>
    );
};
